import axios, { AxiosError } from 'axios';
import { Decimal } from 'decimal.js';
import React, { useState, useCallback } from 'react';
import { preventDefault, formatMoney, getErrorMessagesFromResponse, isStripeError } from '../../../../common/utils';
import { LayoutTitle } from '../Layout';
import { Alert } from '../../../../components/Alert';
import Button from '../../../../components/Button';
import { PromoCodeField } from './PromoCodeField';
import { TaxField } from './TaxField';
export function Order({ fields, updateFields, finishCallback, onLoading, isLoading }) {
    var _a, _b, _c, _d;
    const updatePromoCode = useCallback((promoCode) => {
        updateFields({ promoCode });
    }, [updateFields]);
    const [errors, setErrors] = useState([]);
    const submitPayment = useCallback(preventDefault(async () => {
        var _a, _b, _c;
        if (!(fields === null || fields === void 0 ? void 0 : fields.paymentMethodId)) {
            setErrors(['Enter a payment method']);
            return;
        }
        onLoading(true);
        try {
            const parameters = {
                account_type: fields.accountType,
                first_name: fields.firstName,
                last_name: fields.lastName,
                company: fields.company,
                email: fields.email,
                phone: fields.phone,
                address: fields.address,
                country: fields.country,
                city: fields.city,
                state: fields.state,
                zipcode: fields.zipcode,
                timezone: ((_b = (_a = Intl === null || Intl === void 0 ? void 0 : Intl.DateTimeFormat()) === null || _a === void 0 ? void 0 : _a.resolvedOptions()) === null || _b === void 0 ? void 0 : _b.timeZone) || 'America|Chicago',
                password: fields.password,
                password_confirmation: fields.password_confirmation,
                plan: (_c = fields.selectedPlan) === null || _c === void 0 ? void 0 : _c.id,
                payment_method_id: fields === null || fields === void 0 ? void 0 : fields.paymentMethodId,
                card_name: fields.cardName,
                card_country: fields.cardCountry,
                card_address: fields.cardAddress,
                card_city: fields.cardCity,
                card_state: fields.cardState,
                card_zipcode: fields.cardZipcode,
                promo_code: fields.promoCode.code,
                privacy_accepted: fields.privacyAccepted,
                industry: fields.industry,
                hs_company_id: fields.hsCompanyId,
                hs_contact_id: fields.hsContactId,
            };
            await axios.post('/api/register/create-account', parameters);
            finishCallback();
        }
        catch (error) {
            updateFields({ paymentMethodId: null });
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else if (isStripeError(error)) {
                setErrors([error.message || 'Unknow Stripe error']);
            }
            else {
                setErrors(['Unknown Error']);
                throw error;
            }
            window.scrollTo(0, 300);
        }
        finally {
            onLoading(false);
        }
    }), [fields, finishCallback, onLoading, updateFields]);
    const planPrice = new Decimal(((_a = fields.selectedPlan) === null || _a === void 0 ? void 0 : _a.price) || 0);
    const discountPercentage = (new Decimal(fields.promoCode.discountPercentage)).dividedBy(100);
    const discount = planPrice.mul(discountPercentage);
    const subtotal = planPrice.minus(discount);
    const taxes = (_b = fields.taxes) !== null && _b !== void 0 ? _b : 0;
    const total = subtotal.add(taxes);
    return (React.createElement("div", { className: 'register-payment' },
        React.createElement(LayoutTitle, { text: "Place Order" }),
        React.createElement("form", { className: 'payment-form-wrapper', onSubmit: submitPayment },
            React.createElement("div", { className: 'payment-form-wrapper__column payment-form-wrapper__column--secondary' },
                React.createElement(Alert, { messages: errors, type: 'error' }),
                React.createElement("div", { className: "payment-field" },
                    React.createElement("div", { className: "label" }, "Plan"),
                    React.createElement("div", { className: "value" }, (_c = fields.selectedPlan) === null || _c === void 0 ? void 0 : _c.name)),
                React.createElement("div", { className: "payment-field" },
                    React.createElement("div", { className: "label" }, "Billing"),
                    React.createElement("div", { className: "value" }, "Monthly")),
                React.createElement("div", { className: "payment-field" },
                    React.createElement("div", { className: "label" }, "Total Lines"),
                    React.createElement("div", { className: "value" }, fields.selectedPlan.total_local_numbers + fields.selectedPlan.total_tollfree_numbers)),
                React.createElement("div", { className: 'payment-subtotal' },
                    React.createElement("div", { className: 'label' }, "Subtotal:"),
                    React.createElement("div", { className: 'value' }, formatMoney(planPrice.toNumber()))),
                ((_d = fields.selectedPlan) === null || _d === void 0 ? void 0 : _d.is_trial) ?
                    null :
                    React.createElement(PromoCodeField, { promoCode: fields.promoCode, updatePromoCode: updatePromoCode }),
                fields.promoCode.code && fields.promoCode.discountPercentage > 0 && (React.createElement("div", { className: 'payment-field' },
                    React.createElement("div", { className: 'label' }, "Discount:"),
                    React.createElement("div", { className: 'value' },
                        "- $",
                        discount.toNumber().toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })))),
                React.createElement(TaxField, { fields: fields, updateFields: updateFields }),
                React.createElement("div", { className: 'payment-total' },
                    React.createElement("div", { className: 'label' }, "Total:"),
                    React.createElement("div", { className: 'value' }, fields.selectedPlan && (React.createElement(React.Fragment, null,
                        "$",
                        total.toNumber().toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                        " ",
                        React.createElement("small", null, "USD"))))),
                React.createElement(Button, { className: "payment-submit", variant: "marketing-solid", disabled: isLoading }, "Place Order"),
                React.createElement("img", { src: "/images/secure-payment.svg", alt: "Secure Payment", className: 'secure-payment' })))));
}
